
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export interface PortfolioItemProps {
  title: string;
  subtitle: string;
  image: string;
  link: string;
  linkText?: string;
  videoId?: string;
  instagramId?: string;
  index: number;
  isExternalLink?: boolean;
  isReelsLayout?: boolean;
}

const PortfolioItem: React.FC<PortfolioItemProps> = ({ 
  title, 
  subtitle, 
  image, 
  link, 
  linkText = "ZOBACZ FILM",
  videoId,
  instagramId,
  index,
  isExternalLink = false,
  isReelsLayout = false
}) => {
  const isEven = index % 2 === 0;
  const isMobile = window.innerWidth < 768;
  
  if (isReelsLayout && instagramId) {
    return (
      <div className="overflow-hidden rounded-lg shadow-sm h-full bg-elegant-50 transition-all duration-300 hover:shadow-md">
        <a 
          href={`https://www.instagram.com/p/${instagramId}/`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="block w-full h-full"
        >
          <div className="aspect-[9/16] w-full relative">
            <div className="absolute inset-0 flex items-center justify-center bg-elegant-100">
              <div className="w-full h-full overflow-hidden">
                <iframe 
                  src={`https://www.instagram.com/p/${instagramId}/embed/captioned/?cr=1&v=14&wp=540&rd=https%3A%2F%2Fczuleklisze.pl&rp=%2Fportfolio#%7B%22ci%22%3A0%2C%22os%22%3A2457.699999988079%2C%22ls%22%3A1924.1000000052154%2C%22le%22%3A1941.7999999821186%7D`}
                  className="w-full h-full scale-[1.01] -mt-[55px] -mb-[40px]"
                  frameBorder="0" 
                  title={title}
                  scrolling="no"
                  allowTransparency={true}
                ></iframe>
              </div>
            </div>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="play-button w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-elegant-900 ml-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white">
              <p className="text-center text-sm font-medium">Obejrzyj na Instagramie</p>
            </div>
          </div>
        </a>
      </div>
    );
  }

  const getMediaContent = () => {
    if (videoId) {
      return (
        <div className="aspect-[16/9] w-full">
          <iframe 
            src={`https://player.vimeo.com/video/${videoId}?autoplay=0&muted=0&title=0&byline=0&portrait=0`} 
            className="w-full h-full"
            frameBorder="0" 
            title={title}
            allow="autoplay; fullscreen" 
            allowFullScreen
          ></iframe>
        </div>
      );
    } else if (instagramId) {
      return (
        <div className="aspect-[9/16] w-full max-w-[400px] mx-auto">
          <iframe 
            src={`https://www.instagram.com/p/${instagramId}/embed/`}
            className="w-full h-full"
            frameBorder="0" 
            title={title}
            scrolling="no"
            allowTransparency={true}
          ></iframe>
        </div>
      );
    } else {
      return (
        <div className="aspect-[16/9] w-full">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-full object-cover"
          />
        </div>
      );
    }
  };

  const mediaContent = (
    <div className="relative w-full md:w-1/2 flex justify-center">
      {getMediaContent()}
    </div>
  );

  const textContent = (
    <div className="p-4 md:p-12 flex flex-col justify-center items-center md:items-start w-full md:w-1/2 text-center md:text-left md:pl-16">
      <h3 className="text-lg md:text-2xl font-serif text-elegant-900 tracking-tight mb-2 md:mb-3">{title}</h3>
      <p className="text-elegant-600 text-sm md:text-base mb-3 md:mb-5 font-light">{subtitle}</p>
      {!videoId && !instagramId && (
        <span className="group inline-flex items-center text-navy-800 hover:text-navy-600 font-medium uppercase text-xs md:text-sm tracking-wider mt-2">
          {linkText || "ZOBACZ ZDJĘCIA"} <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
        </span>
      )}
    </div>
  );

  if (isMobile) {
    if (videoId || instagramId) {
      return (
        <div className="flex flex-col overflow-hidden bg-elegant-50 rounded-lg shadow-sm">
          <div className="w-full">
            {getMediaContent()}
          </div>
          <a href={link} target="_blank" rel="noopener noreferrer" className="p-4">
            <h3 className="text-lg font-serif text-elegant-900 tracking-tight mb-1">{title}</h3>
            <p className="text-elegant-600 text-xs font-light">{subtitle}</p>
          </a>
        </div>
      );
    }

    if (isExternalLink) {
      return (
        <a href={link} target="_blank" rel="noopener noreferrer" className="block w-full cursor-pointer transition-all duration-300 hover:shadow-md rounded-lg overflow-hidden bg-elegant-50 shadow-sm">
          <div className="aspect-[4/3] w-full">
            <img 
              src={image} 
              alt={title} 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-4">
            <h3 className="text-lg font-serif text-elegant-900 tracking-tight mb-1">{title}</h3>
            <p className="text-elegant-600 text-xs font-light">{subtitle}</p>
            <span className="group inline-flex items-center text-navy-800 hover:text-navy-600 font-medium uppercase text-xs tracking-wider mt-2">
              {linkText || "ZOBACZ ZDJĘCIA"} <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </a>
      );
    }

    return (
      <Link to={link} className="block w-full cursor-pointer transition-all duration-300 hover:shadow-md rounded-lg overflow-hidden bg-elegant-50 shadow-sm">
        <div className="aspect-[4/3] w-full">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-4">
          <h3 className="text-lg font-serif text-elegant-900 tracking-tight mb-1">{title}</h3>
          <p className="text-elegant-600 text-xs font-light">{subtitle}</p>
          <span className="group inline-flex items-center text-navy-800 hover:text-navy-600 font-medium uppercase text-xs tracking-wider mt-2">
            {linkText || "ZOBACZ ZDJĘCIA"} <ArrowRight className="ml-2 h-3 w-3 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    );
  }

  if (videoId || instagramId) {
    return (
      <div className="flex flex-col md:flex-row items-center overflow-hidden bg-elegant-50 h-auto">
        {isEven ? (
          <>
            {mediaContent}
            <a href={link} target="_blank" rel="noopener noreferrer" className="w-full md:w-1/2">
              {textContent}
            </a>
          </>
        ) : (
          <>
            <a href={link} target="_blank" rel="noopener noreferrer" className="w-full md:w-1/2">
              {textContent}
            </a>
            {mediaContent}
          </>
        )}
      </div>
    );
  }

  if (isExternalLink) {
    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="block w-full cursor-pointer transition-all duration-300 hover:shadow-lg">
        <div className="flex flex-col md:flex-row items-center overflow-hidden bg-elegant-50 h-auto">
          {isEven ? (
            <>
              {mediaContent}
              {textContent}
            </>
          ) : (
            <>
              {textContent}
              {mediaContent}
            </>
          )}
        </div>
      </a>
    );
  }

  return (
    <Link to={link} className="block w-full cursor-pointer transition-all duration-300 hover:shadow-lg">
      <div className="flex flex-col md:flex-row items-center overflow-hidden bg-elegant-50 h-auto">
        {isEven ? (
          <>
            {mediaContent}
            {textContent}
          </>
        ) : (
          <>
            {textContent}
            {mediaContent}
          </>
        )}
      </div>
    </Link>
  );
};

export default PortfolioItem;
